import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Cookies from 'js-cookie';

const TARGETS_API = 'https://system.sales-management.cloud/api/targets.php';
const FIELD_DEFINITIONS_API = 'https://system.sales-management.cloud/api/report-field-definitions.php';

const toYearMonth = (monthValue) => {
  const [year, month] = monthValue.split('-').map(Number);
  return { year, month };
};

function ReportGoals() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [targets, setTargets] = useState({});
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [actual, setActual] = useState({});
  const [achievement, setAchievement] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldLoading, setFieldLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const dynamicTargetFields = useMemo(() => {
    return fieldDefinitions
      .filter(field => field.field_type === 'number')
      .sort((a, b) => (a.field_order || 0) - (b.field_order || 0));
  }, [fieldDefinitions]);

  const fieldStateKey = useCallback((field) => `field_${field.field_id}`, []);

  const fetchFieldDefinitions = useCallback(async () => {
    setFieldLoading(true);
    try {
      const token = Cookies.get('auth_token');
      if (!token) return;

      const response = await fetch(FIELD_DEFINITIONS_API, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.status === 'success') {
        setFieldDefinitions(data.data?.fields || []);
      }
    } catch (err) {
      console.error('Failed to fetch report field definitions:', err);
    } finally {
      setFieldLoading(false);
    }
  }, []);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const { year, month } = toYearMonth(selectedMonth);
      const response = await fetch(`${TARGETS_API}?year=${year}&month=${month}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.status === 'success') {
        const targetItems = Array.isArray(data.data?.target_items) ? data.data.target_items : [];

        const nextTargets = {};
        dynamicTargetFields.forEach(field => {
          const key = fieldStateKey(field);
          const matched = targetItems.find(item => {
            if (item.field_id && Number(item.field_id) === Number(field.field_id)) {
              return true;
            }
            if (item.target_label && String(item.target_label) === String(field.field_name)) {
              return true;
            }
            return false;
          });
          nextTargets[key] = Number(matched?.target_value || 0);
        });

        setTargets(nextTargets);
        setActual(data.data?.actual || {});
        setAchievement(data.data?.achievement || {});
      } else {
        setError(data.message || '目標データの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch targets:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, dynamicTargetFields, fieldStateKey]);

  useEffect(() => {
    fetchFieldDefinitions();
  }, [fetchFieldDefinitions]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const { year, month } = toYearMonth(selectedMonth);
      const payload = {
        year,
        month,
        targets: dynamicTargetFields.map(field => {
          const key = fieldStateKey(field);
          return {
            field_id: Number(field.field_id),
            target_key: key,
            target_label: field.field_name,
            target_value: Number(targets[key] || 0),
          };
        })
      };

      const response = await fetch(TARGETS_API, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('目標を保存しました');
        fetchTargets();
      } else {
        setError(data.message || '目標の保存に失敗しました');
      }
    } catch (err) {
      console.error('Failed to save targets:', err);
      setError('通信エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">日報目標設定</h1>
        <p className="mt-2 text-gray-600">本人の日報項目に対する月次目標を設定できます。</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">対象月</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md text-sm text-green-600">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">目標入力</h2>
          {(loading || fieldLoading) ? (
            <p className="text-gray-500">読み込み中...</p>
          ) : dynamicTargetFields.length === 0 ? (
            <p className="text-sm text-gray-600">目標に紐づく数値フィールドがありません。日報フィールド設定で数値項目を設定してください。</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">日報フィールド設定から取得した {dynamicTargetFields.length} 個の数値項目を表示しています。</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dynamicTargetFields.map((field) => {
                  const key = fieldStateKey(field);
                  return (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{field.field_name}</label>
                      <input
                        type="number"
                        min="0"
                        value={targets[key] ?? 0}
                        onChange={(e) => {
                          const parsed = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                          setTargets(prev => ({ ...prev, [key]: parsed }));
                          setError('');
                          setSuccess('');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">実績サマリー</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-gray-600">売上実績（完了）</p>
              <p className="text-xl font-semibold text-gray-900">{Number(actual.completed_sales || 0).toLocaleString()} 円</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-gray-600">アポ実績</p>
              <p className="text-xl font-semibold text-gray-900">{Number(actual.appointment_count || 0).toLocaleString()} 件</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-md">
              <p className="text-gray-600">売上達成率</p>
              <p className="text-xl font-semibold text-blue-700">{achievement.sales_rate != null ? `${Number(achievement.sales_rate).toFixed(1)}%` : '-'}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-md">
              <p className="text-gray-600">アポ達成率</p>
              <p className="text-xl font-semibold text-blue-700">{achievement.appointment_rate != null ? `${Number(achievement.appointment_rate).toFixed(1)}%` : '-'}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading || dynamicTargetFields.length === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? '保存中...' : '目標を保存'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReportGoals;
